/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Layout, Select, Row, Skeleton, Tooltip } from "antd";
import "./workflow.css";
import TableView from "./TableView";
import { InfoCircleOutlined } from "@ant-design/icons";

import { journeys } from "../../apiService/blustreamApi";
import { journeyFromBeta, touchpointFromBeta } from "./adapters";
import { runWithConcurrency } from "./concurrency";
import { Label } from "aws-amplify-react";
import touchpointSVGNew from "./../../assets/Images/touchpointSVGNew.svg";


const { Header, Content } = Layout;
const { Option } = Select;

const PAGE_SIZE = 50;
const TOUCHPOINT_PRELOAD_CONCURRENCY = 10;
const TOUCHPOINT_PAGE_SIZE = 200;
const SCROLL_LOAD_THRESHOLD_PX = 32;

// Pagination of touchpoints within a single journey is deferred to Epic 3
// (story ch59926 acceptance notes). For now we fetch one large page and warn
// if any journey has more than the cap so the gap shows up in logs instead of
// truncating silently.
function warnIfTouchpointPageCapped(journeyId, raw, tpPage) {
  const totalElements = tpPage?.page?.total_elements;
  const isLast = tpPage?.page?.last;
  if (raw.length >= TOUCHPOINT_PAGE_SIZE && isLast === false) {
    // eslint-disable-next-line no-console
    console.warn(
      '[touchpoints] journey',
      journeyId,
      'has more touchpoints than the FE page cap; only the first',
      TOUCHPOINT_PAGE_SIZE,
      'are rendered (total reported by API:',
      totalElements,
      '). Pagination on touchpoints within a journey is deferred to Epic 3.'
    );
  }
}

const WorkFlow = () => {
  // Paginated journey accumulator
  const [journeyList, setJourneyList] = useState([]); // legacy-shape journeys
  const [nextPage, setNextPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [journeysLoading, setJourneysLoading] = useState(true);
  const initialLoadDone = useRef(false);

  // Touchpoint cache: { [journey_id]: touchpoint[] (legacy shape) }
  const [touchpointCache, setTouchpointCache] = useState({});

  const [selectedJourneyId, setSelectedJourneyId] = useState();
  const [searchTerm, setSearchTerm] = useState('');

  const preloadCtrlsRef = useRef([]);
  const inFlightRef = useRef(false);

  // Load next journey page + kick off touchpoint preload for the new batch.
  const loadNextJourneyPage = useCallback(async () => {
    if (!hasMore || inFlightRef.current) return;
    inFlightRef.current = true;
    setJourneysLoading(true);

    try {
      const result = await journeys.list({ page: nextPage, size: PAGE_SIZE });
      const content = result?.content || [];
      const pageMeta = result?.page;
      const adapted = content.map(journeyFromBeta);

      setJourneyList((prev) => {
        // Map-based dedup by journey_id. Latest wins so a re-fetched journey
        // picks up any name/state changes from the backend.
        const byId = new Map();
        for (const j of prev) {
          if (j?.journey_id != null) byId.set(j.journey_id, j);
        }
        let collisions = 0;
        for (const j of adapted) {
          if (j?.journey_id == null) continue;
          if (byId.has(j.journey_id)) collisions += 1;
          byId.set(j.journey_id, j);
        }
        if (collisions > 0) {
          // Backend paged response returned IDs already seen — likely a
          // missing stable secondary sort key. Worth flagging upstream.
          // eslint-disable-next-line no-console
          console.warn('[touchpoints] journeys page returned', collisions, 'already-seen journey_id(s)');
        }
        return Array.from(byId.values()).sort((a, b) =>
          (a.name ?? '').toLowerCase().localeCompare((b.name ?? '').toLowerCase())
        );
      });

      // Auto-select first journey on initial load (preserves legacy UX)
      if (!initialLoadDone.current && adapted.length > 0) {
        setSelectedJourneyId(adapted[0].journey_id);
      }
      initialLoadDone.current = true;

      const isLastPage = pageMeta?.last === true || content.length < PAGE_SIZE;
      setHasMore(!isLastPage);
      setNextPage((p) => p + 1);

      // Background preload touchpoints for this batch. Each batch runs to
      // completion independently — newer batches do NOT abort older ones, so
      // a fast scroll through dropdown pages doesn't leave earlier journeys
      // with empty cache entries.
      if (content.length > 0) {
        const ctrl = new AbortController();
        preloadCtrlsRef.current.push(ctrl);

        const jobs = content.map((j) => async () => {
          if (touchpointCacheRef.current[j.journey_id] !== undefined) return;
          try {
            const tpPage = await journeys.listTouchpoints(j.journey_id, {
              page: 0,
              size: TOUCHPOINT_PAGE_SIZE,
              signal: ctrl.signal,
            });
            const raw = tpPage?.content || [];
            warnIfTouchpointPageCapped(j.journey_id, raw, tpPage);
            const adaptedTps = raw
              .slice()
              .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
              .map((t) => touchpointFromBeta(t, j.journey_id));
            setTouchpointCache((prev) => ({ ...prev, [j.journey_id]: adaptedTps }));
          } catch (e) {
            if (e?.name === 'AbortError') return;
            // Persist empty array so cache-miss skeleton doesn't loop forever.
            // eslint-disable-next-line no-console
            console.error('Preload touchpoints failed', j.journey_id, e);
            setTouchpointCache((prev) => ({ ...prev, [j.journey_id]: [] }));
          }
        });

        runWithConcurrency(jobs, TOUCHPOINT_PRELOAD_CONCURRENCY, ctrl.signal);
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      // eslint-disable-next-line no-console
      console.error('Failed to load journeys page', err);
    } finally {
      inFlightRef.current = false;
      setJourneysLoading(false);
    }
  }, [hasMore, nextPage]);

  useEffect(() => {
    loadNextJourneyPage();
    return () => {
      preloadCtrlsRef.current.forEach((c) => c.abort());
      preloadCtrlsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPopupScroll = (e) => {
    const target = e.target;
    if (!target) return;
    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceToBottom < SCROLL_LOAD_THRESHOLD_PX && hasMore && !journeysLoading) {
      loadNextJourneyPage();
    }
  };

  const handleOption = (option) => {
    setSelectedJourneyId(option);
  };

  const selectedJourney = useMemo(
    () => journeyList.find((j) => j.journey_id === selectedJourneyId),
    [journeyList, selectedJourneyId]
  );

  const selectedTouchpoints = touchpointCache[selectedJourneyId];
  const touchpointCacheRef = useRef(touchpointCache);
  touchpointCacheRef.current = touchpointCache;
  const requestedTouchpointIdsRef = useRef(new Set());

  // On-demand fetch when the selected journey has no cache entry yet
  // (later-page journey not yet preloaded, or a failed preload). Without this,
  // the table sits on a skeleton forever for cache misses.
  useEffect(() => {
    if (selectedJourneyId === undefined) return;
    if (touchpointCacheRef.current[selectedJourneyId] !== undefined) return;
    if (requestedTouchpointIdsRef.current.has(selectedJourneyId)) return;
    requestedTouchpointIdsRef.current.add(selectedJourneyId);

    const ctrl = new AbortController();

    (async () => {
      try {
        const tpPage = await journeys.listTouchpoints(selectedJourneyId, {
          page: 0,
          size: TOUCHPOINT_PAGE_SIZE,
          signal: ctrl.signal,
        });
        const raw = tpPage?.content || [];
        warnIfTouchpointPageCapped(selectedJourneyId, raw, tpPage);
        const adaptedTps = raw
          .slice()
          .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
          .map((t) => touchpointFromBeta(t, selectedJourneyId));
        setTouchpointCache((prev) => ({ ...prev, [selectedJourneyId]: adaptedTps }));
      } catch (err) {
        if (err?.name === 'AbortError') {
          requestedTouchpointIdsRef.current.delete(selectedJourneyId);
          return;
        }
        // Persist empty array so the table doesn't sit on a skeleton.
        // eslint-disable-next-line no-console
        console.error('Failed to load touchpoints for journey', selectedJourneyId, err);
        setTouchpointCache((prev) => ({ ...prev, [selectedJourneyId]: [] }));
      }
    })();

    return () => ctrl.abort();
  }, [selectedJourneyId]);

  // Called by TableView after a successful PATCH so the cache stays current.
  const onTouchpointUpdated = useCallback((journeyId, touchpointId, updated) => {
    setTouchpointCache((prev) => {
      const list = prev[journeyId] || [];
      return {
        ...prev,
        [journeyId]: list.map((t) => (t.id === touchpointId ? { ...t, ...updated } : t)),
      };
    });
  }, []);

  const generateFriendlyTouchpointCount = (count) => {
    if (count === undefined || count === null) return "";
    if (count === 1) return `( ${count} Touchpoint )`;
    return `( ${count} Touchpoints )`;
  };

  const isInitialLoading = journeysLoading && !initialLoadDone.current;
  const isTableLoading = selectedJourneyId !== undefined && selectedTouchpoints === undefined;

  return (
    <>
      <div
        className="workFlow"
        style={{
          width: "100%",
          borderRadius: "8px",
          height: "100%",
        }}
      >
        <Layout>
          <Header style={{ backgroundColor: "white" }}>
            <Row className="workflow_header">
              <div
                style={{
                  paddingLeft: "20px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "100%",
                  zIndex: 200,
                }}
              >
                <h2
                  style={{
                    fontWeight: "700",
                    fontSize: "22px",
                    color: "#464646",
                    lineHeight: "1.5",
                    marginTop: "30px",
                    marginBottom: "20px",
                  }}
                >
                  Welcome to Blustream{" "}
                  <span style={{ color: "#F37064" }}>Touchpoint</span> Manager!
                </h2>
                {isInitialLoading ? (
                  <Skeleton.Button
                    active
                    style={{ marginLeft: "110px", width: 250 }}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginRight: 8,
                      alignItems: "center",
                    }}
                  >
                    <Label
                      style={{
                        lineHeight: "1",
                        marginRight: "3px",
                        fontSize: "16px",
                      }}
                    >
                      Select Journey :{" "}
                    </Label>
                    <Select
                      showSearch
                      allowClear
                      className="standard-dropdown"
                      style={{
                        minWidth: 250,
                        width: "50vw",
                        background: "transparent",
                      }}
                      placeholder="Select Journey"
                      optionFilterProp="children"
                      value={selectedJourney?.name || searchTerm}
                      onChange={handleOption}
                      onClear={() => {
                        setSearchTerm('');
                      }}
                      onPopupScroll={onPopupScroll}
                      filterSort={(optionA, optionB) =>
                        (optionA?.name ?? '').toLowerCase().localeCompare((optionB?.name ?? '').toLowerCase())
                      }
                      filterOption={(input, option) =>
                        (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          {journeysLoading && hasMore && (
                            <div style={{ padding: 8, textAlign: 'center' }}>
                              <Skeleton.Button active size="small" style={{ width: '100%' }} />
                            </div>
                          )}
                        </>
                      )}
                    >
                      {journeyList.map((journeyInfo, index) => {
                        const count = touchpointCache[journeyInfo.journey_id]?.length;
                        return (
                          <Option
                            key={journeyInfo.journey_id ?? index}
                            value={journeyInfo.journey_id}
                            name={journeyInfo.name}
                            style={
                              journeyInfo.journey_id === selectedJourneyId
                                ? {
                                    backgroundColor: "#f5f5f5",
                                    fontWeight: "bold",
                                  }
                                : {}
                            }
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <p style={{ marginBottom: 0, marginLeft: 10 }}>
                                {journeyInfo.name}
                              </p>
                              <span style={{ marginLeft: "auto", color: "#959595", fontSize: "smaller" }}>
                                {generateFriendlyTouchpointCount(count)}
                              </span>
                            </div>
                          </Option>
                        );
                      })}
                    </Select>
                    <Tooltip
                      placement="bottom"
                      title="Journeys are a series of touchpoints that send more messages. Contacts start new journeys when they answer a questions or you associate them with a product."
                    >
                      <InfoCircleOutlined style={{ color: "#959595" }} />
                    </Tooltip>
                  </div>
                )}
              </div>
              <div className="wrapper">
                <img src={touchpointSVGNew} alt="" />
              </div>
            </Row>
          </Header>

          <Layout>
            {isInitialLoading || isTableLoading ? (
              <div
                className="p-20"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <Skeleton active paragraph={{ rows: 5 }} />
              </div>
            ) : (
              <Content>
                <TableView
                  journey={selectedJourney}
                  touchpoints={selectedTouchpoints || []}
                  journeyId={selectedJourneyId}
                  onTouchpointUpdated={onTouchpointUpdated}
                />
              </Content>
            )}
          </Layout>
        </Layout>
      </div>
    </>
  );
};

export default WorkFlow;
